import { styleFromFontWeight } from "@redotech/redo-content-builder-common/brand-kit";
import {
  alignmentToFlexAlign,
  DiscountCodeElement,
} from "@redotech/redo-content-builder-common/element-schema";
import CheckIcon from "@redotech/redo-icons/check.svg";
import classNames from "classnames";
import { memo, useState } from "react";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { RedoSpinner } from "../../arbiter-components/loading/redo-spinner";
import { copyToClipboard } from "../../clipboard";
import { Flex } from "../../flex";
import { getBaseElementStyles } from "./base-element-styles";
import * as styles from "./discount-code.module.css";
import { FormElementProps } from "./index";

export const SignupFormDiscountCodeDisplay = memo(function DiscountCodeDisplay(
  props: FormElementProps<DiscountCodeElement>,
) {
  const [showCheck, setShowCheck] = useState(false);
  const context = props.discountContext ?? { type: "placeholder" };

  // Check if this element has a specific discountId configured and resolve it
  const resolvedDiscountCode =
    props.element.discountId &&
    props.closeActionContext?.resolvedDiscounts?.[props.element.discountId];

  const getDisplayCode = (): string => {
    // If element has a configured discountId with a resolved code, use it
    if (resolvedDiscountCode) {
      return resolvedDiscountCode;
    }

    switch (context.type) {
      case "active":
        return context.discountCode;
      case "preview":
        return context.isLoading ? "" : context.previewCode || "XXX-XXXX";
      case "placeholder":
        return "XXX-XXXX";
    }
  };

  const isLoading = context.type === "preview" && context.isLoading;
  const displayCode = getDisplayCode();

  const handleCopy = async (text: string) => {
    await copyToClipboard(text);
    setShowCheck(true);
    setTimeout(() => setShowCheck(false), 1500);
  };

  const padding = props.element.sectionPadding;
  const gap = 8; // Fixed gap between outer and inner elements
  const outerRadius = props.element.borderRadius;
  const innerRadius = Math.max(0, outerRadius - gap);
  const baseStyles = getBaseElementStyles(props.element);

  return (
    <div style={baseStyles}>
      <div
        style={{
          backgroundColor: props.element.sectionColor,
          paddingTop: `${padding.top}px`,
          paddingRight: `${padding.right}px`,
          paddingBottom: `${padding.bottom}px`,
          paddingLeft: `${padding.left}px`,
          borderRadius: `${outerRadius}px`,
        }}
      >
        <div
          style={{
            backgroundColor: props.element.blockBackgroundColor,
            borderRadius: `${innerRadius}px`,
            padding: "var(--redo-spacing-md)",
          }}
        >
          <Flex
            align="center"
            aria-label={isLoading ? undefined : "Copy discount code"}
            cursor={isLoading ? "default" : "pointer"}
            onClick={isLoading ? undefined : () => handleCopy(displayCode)}
            onKeyDown={
              isLoading
                ? undefined
                : (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      void handleCopy(displayCode);
                    }
                  }
            }
            role={isLoading ? undefined : "button"}
            style={{
              justifyContent: alignmentToFlexAlign[props.element.alignment],
              position: "relative",
            }}
            tabIndex={isLoading ? undefined : 0}
          >
            {isLoading ? (
              <RedoSpinner size="xs" style="track" />
            ) : (
              <>
                <div
                  className={classNames(
                    styles.discountText,
                    showCheck
                      ? styles.discountTextHidden
                      : styles.discountTextVisible,
                  )}
                  style={{
                    color: props.element.textColor,
                    fontFamily: `'${props.element.fontFamily}'`,
                    fontSize: `${props.element.fontSize}px`,
                    fontWeight: styleFromFontWeight(props.element.fontWeight),
                  }}
                >
                  {displayCode}
                </div>
                {showCheck && (
                  <RedoIcon
                    className={styles.checkIcon}
                    color="success"
                    Icon={CheckIcon}
                    size={20}
                  />
                )}
              </>
            )}
          </Flex>
        </div>
      </div>
    </div>
  );
});
